import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listShippingFees, type ShippingFee } from '@/services/payments.service';
import type { ProductLine } from './PaymentProductsSection';

export interface AppliedShippingFee {
  id: number;
  name: string;
  amount: number;
  /** True when this fee came from an existing payment rather than the current lines. */
  fromExisting: boolean;
}

export interface UseShippingFeesArgs {
  /** Current product lines; each carries the shipping fee its catalog item declares. */
  productLines: ProductLine[];
  /**
   * Shipping already recorded on the payment being edited. These are snapshots of
   * what was charged, so they win over the catalog's current amount for display —
   * reopening a payment must show what the patient actually paid.
   */
  existingFees?: Array<{ shippingFeeID: number; name: string; amount: number }>;
  /**
   * Changes whenever the modal opens/closes or switches payment, resetting the
   * staff's add/remove choices so they never leak into the next payment.
   */
  resetKey?: unknown;
  /** Skip the catalog fetch when the section is not in use (read-only views). */
  enabled?: boolean;
}

export interface UseShippingFeesResult {
  /** The fees to charge, one per distinct fee. */
  appliedFees: AppliedShippingFee[];
  shippingTotal: number;
  /** Active account fees not currently applied — the "Add shipping" options. */
  availableFees: ShippingFee[];
  removeFee: (id: number) => void;
  addFee: (id: number) => void;
  /** Ready for `CreatePatientPaymentPayload.shipping`. */
  payload: Array<{ shippingFeeId: number }>;
}

/**
 * Decides which shipping fees a payment should carry.
 *
 * Shipping is charged **once per distinct fee**, not per unit and not per
 * product: five peptides sharing a $25 fee cost $25, and adding a supply product
 * with its own $15 fee makes it $40. Fees are auto-derived from the products on
 * the payment; staff may remove one or add one back, but never edit the amount
 * (the dollar figure is resolved server-side from `shipping_fees` on save, so a
 * tampered payload cannot change what shipping costs).
 *
 * A removed fee is remembered for the life of the modal, so adding another
 * product from the same group does not silently resurrect it.
 */
export function useShippingFees({
  productLines,
  existingFees,
  resetKey,
  enabled = true,
}: UseShippingFeesArgs): UseShippingFeesResult {
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);
  const [manualIds, setManualIds] = useState<number[]>([]);

  // Staff choices are per-modal-session; clear them when the modal reopens or
  // targets a different payment.
  useEffect(() => {
    setDismissedIds([]);
    setManualIds([]);
  }, [resetKey]);

  const { data: catalogFees = [] } = useQuery({
    queryKey: ['shipping-fees'],
    queryFn: () => listShippingFees(),
    enabled,
  });

  // Snapshot amounts from the payment being edited, keyed by fee id.
  const existingById = useMemo(() => {
    const map = new Map<number, { name: string; amount: number }>();
    for (const fee of existingFees ?? []) {
      if (fee.shippingFeeID > 0) {
        map.set(fee.shippingFeeID, { name: fee.name, amount: fee.amount });
      }
    }
    return map;
  }, [existingFees]);

  const catalogById = useMemo(() => {
    const map = new Map<number, ShippingFee>();
    for (const fee of catalogFees) map.set(fee.shippingFeeID, fee);
    return map;
  }, [catalogFees]);

  const appliedFees = useMemo<AppliedShippingFee[]>(() => {
    const dismissed = new Set(dismissedIds);
    const ordered: number[] = [];
    const seen = new Set<number>();

    const consider = (id: number) => {
      if (id <= 0 || seen.has(id) || dismissed.has(id)) return;
      seen.add(id);
      ordered.push(id);
    };

    // Already on the payment first, so an edited payment keeps its original order.
    for (const id of existingById.keys()) consider(id);
    // Then whatever the current product lines imply.
    for (const line of productLines) {
      if (line.shippingFee) consider(line.shippingFee.id);
    }
    // Then anything staff added by hand.
    for (const id of manualIds) consider(id);

    return ordered.flatMap((id) => {
      const snapshot = existingById.get(id);
      const catalog = catalogById.get(id);
      const fromLine = productLines.find((l) => l.shippingFee?.id === id)?.shippingFee ?? null;

      // Prefer the charged snapshot, then the catalog, then whatever the line
      // carried. A fee we can resolve nothing for is dropped rather than shown
      // at $0 — that means it was retired and is no longer chargeable.
      const name = snapshot?.name ?? catalog?.name ?? fromLine?.name ?? '';
      const amount = snapshot?.amount ?? catalog?.amount ?? fromLine?.amount ?? 0;
      if (amount <= 0) return [];

      return [{ id, name, amount, fromExisting: snapshot != null }];
    });
  }, [productLines, existingById, catalogById, dismissedIds, manualIds]);

  const shippingTotal = useMemo(
    () => Math.round(appliedFees.reduce((sum, fee) => sum + fee.amount, 0) * 100) / 100,
    [appliedFees],
  );

  const availableFees = useMemo(() => {
    const applied = new Set(appliedFees.map((f) => f.id));
    return catalogFees.filter((fee) => fee.active && !applied.has(fee.shippingFeeID));
  }, [catalogFees, appliedFees]);

  const removeFee = useCallback((id: number) => {
    setManualIds((prev) => prev.filter((existing) => existing !== id));
    setDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const addFee = useCallback((id: number) => {
    if (id <= 0) return;
    setDismissedIds((prev) => prev.filter((existing) => existing !== id));
    setManualIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const payload = useMemo(
    () => appliedFees.map((fee) => ({ shippingFeeId: fee.id })),
    [appliedFees],
  );

  return { appliedFees, shippingTotal, availableFees, removeFee, addFee, payload };
}
